import { useThreadSidebar } from '@/context/ThreadSidebarContext';
import { isReasonerPath } from '@/helpers/navBar.helpers';
import { Tooltip } from 'primereact/tooltip';
import { Route, BarChart3 } from 'lucide-react';
import { BiNetworkChart } from 'react-icons/bi';
import { PiBrainLight, PiRobotLight } from 'react-icons/pi';
import { NavLink, useLocation } from 'react-router';

const NavBar = () => {
  const location = useLocation();
  const { isSidebarVisible, toggleSidebar } = useThreadSidebar();
  const isReasonerActive = isReasonerPath(location.pathname);
  // reDesign is the mapping panel. The older workflow-builder screens still
  // answer under /reDesign while they wait to be removed, so the icon stays
  // lit on any of them rather than only on the one it links to.
  const isReDesignActive = location.pathname.startsWith('/reDesign');

  const handleReasonerClick = (e: React.MouseEvent) => {
    if (isReasonerActive) {
      e.preventDefault();
      toggleSidebar();
    }
  };

  return (
    <nav className='flex flex-col gap-6'>
      <Tooltip target='.tooltip-message' showDelay={500} />
      <NavLink
        to='/reVeal'
        onClick={handleReasonerClick}
        className={({ isActive }) =>
          `p-3 rounded-lg tooltip-message theme-transition group relative
           ${isActive
            ? 'shadow-md border-2 border-border'
            : 'hover:bg-accent hover:text-accent-foreground'
          }`
        }
        data-pr-tooltip={
          isReasonerActive && !isSidebarVisible
            ? 'reVeal (Show Threads)'
            : 'reVeal'
        }
      >
        <div className='relative'>
          <PiRobotLight
            className={`w-6 h-6 transition-opacity ${isReasonerActive && !isSidebarVisible
              ? 'group-hover:opacity-0'
              : ''
              }`}
          />
          {isReasonerActive && !isSidebarVisible && (
            <svg
              className='w-6 h-6 absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5l7 7-7 7'
              />
            </svg>
          )}
        </div>
      </NavLink>

      <NavLink
        to='/reDesign/mapping'
        className={({ isActive }) =>
          `p-3 rounded-lg tooltip-message theme-transition group relative flex items-center justify-center
           ${isActive || isReDesignActive
            ? 'bg-primary text-primary-foreground shadow-md border-2 border-border'
            : 'hover:bg-accent hover:text-accent-foreground'
          }`
        }
        data-pr-tooltip='reDesign'
      >
        <Route className='w-5 h-5' />
        <div className='absolute left-full ml-2 w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity' />
      </NavLink>

      <NavLink
        to='/reEnrich'
        className={({ isActive }) =>
          `p-3 rounded-lg tooltip-message theme-transition group relative flex items-center justify-center
           ${isActive
            ? 'bg-primary text-primary-foreground shadow-md border-2 border-border'
            : 'hover:bg-accent hover:text-accent-foreground'
          }`
        }
        data-pr-tooltip='reEnrich'
      >
        <PiBrainLight className='w-5.5 h-5.5' />
        <div className='absolute left-full ml-2 w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity' />
      </NavLink>

      <NavLink
        to='/reMap'
        className={({ isActive }) =>
          `p-3 rounded-lg tooltip-message theme-transition group relative
           ${isActive
            ? 'bg-primary text-primary-foreground shadow-md border-2 border-border'
            : 'hover:bg-accent hover:text-accent-foreground'
          }`
        }
        data-pr-tooltip='reMap'
      >
        <BiNetworkChart className='w-6 h-6' />
        <div className='absolute left-full ml-2 w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity' />
      </NavLink>

      <NavLink
        to='/reFocus'
        className={({ isActive }) =>
          `p-3 rounded-lg tooltip-message theme-transition group relative
           ${isActive
            ? 'bg-primary text-primary-foreground shadow-md border-2 border-border'
            : 'hover:bg-accent hover:text-accent-foreground'
          }`
        }
        data-pr-tooltip='reFocus'
      >
        <BarChart3 className='w-6 h-6' />
        <div className='absolute left-full ml-2 w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity' />
      </NavLink>
    </nav>
  );
};

export default NavBar;
